import { useTranslation } from 'react-i18next';
import { LegalLayout, Section, Bullets, Bullet } from './LegalLayout';

export function TermsPage() {
  const { t } = useTranslation();
  return (
    <LegalLayout
      eyebrow={t('terms.eyebrow', { defaultValue: 'Terms' }) as string}
      title={t('terms.title', { defaultValue: 'Terms of Service' })}
      updated="2026-05-23"
    >
      <Section id="agreement" kicker="01" title={t('terms.s1.title', { defaultValue: 'The agreement' }) as string}>
        <p>
          {t('terms.s1.body', {
            defaultValue:
              'By creating an account or being added to a workspace, you accept this agreement with Knowlez Inc. It governs your use of knowlez.com and the Knowlez knowledge platform (the "service"). If you accept on behalf of an employer, you confirm you have the authority to bind them.',
          })}
        </p>
      </Section>

      <Section id="account" kicker="02" title={t('terms.s2.title', { defaultValue: 'Your account' }) as string}>
        <Bullets>
          <Bullet>{t('terms.s2.b1', { defaultValue: 'You are responsible for keeping your sign-in credentials confidential. We strongly recommend using single sign-on with your employer\'s IdP once you are on the Enterprise tier.' })}</Bullet>
          <Bullet>{t('terms.s2.b2', { defaultValue: 'You agree to use a real, monitored email address; we send security-relevant messages there (password resets, suspicious sign-in alerts, retention-expiry notices).' })}</Bullet>
          <Bullet>{t('terms.s2.b3', { defaultValue: 'You can close your account at any time from Settings. Account closure deletes your personal identifiers within 30 days; workspace content owned by other members of your company is unaffected.' })}</Bullet>
        </Bullets>
      </Section>

      <Section id="content" kicker="03" title={t('terms.s3.title', { defaultValue: 'Who owns what' }) as string}>
        <p>{t('terms.s3.intro', { defaultValue: 'Two clean lines:' })}</p>
        <Bullets>
          <Bullet><strong>Your content stays yours.</strong> Anything you upload, anything pulled in through a connector, every conversation transcript, every brief — belongs to your workspace. Knowlez has only the license required to operate the service: index, retrieve, route to your chosen LLM, present back to you, and store for the retention window your workspace owner has configured.</Bullet>
          <Bullet><strong>The product is ours.</strong> The Knowlez software, designs, brand, documentation, and APIs are owned by Knowlez Inc. You may use them only as the service permits. The chunk + entity + edge data-model spec is open-sourced separately under MIT and is the exception.</Bullet>
        </Bullets>
      </Section>

      <Section id="ai" kicker="04" title={t('terms.s4.title', { defaultValue: 'AI output' }) as string}>
        <p>
          {t('terms.s4.body', {
            defaultValue:
              'Knowlez answers questions by retrieving chunks from your indexed corpus and asking your chosen LLM to compose an answer that cites them. The output is best-effort. Review every answer before relying on it for a decision. The cited chunks are authoritative; the prose around them is the model\'s synthesis.',
          })}
        </p>
        <p>
          {t('terms.s4.training', {
            defaultValue:
              'We do not train AI models on your content. We route AI calls through our model gateway using the tier your workspace owner selects; that traffic is governed by the underlying provider\'s API terms — every supported provider guarantees no-training for paid API traffic.',
          })}
        </p>
      </Section>

      <Section id="acceptable" kicker="05" title={t('terms.s5.title', { defaultValue: 'Acceptable use' }) as string}>
        <p>{t('terms.s5.intro', { defaultValue: 'Knowlez is for legitimate knowledge work. You agree not to use it to:' })}</p>
        <Bullets>
          <Bullet>{t('terms.s5.b1', { defaultValue: 'Process content you do not have a legal right to (third-party trade secrets, intellectual property you have not licensed, personal data of people who have not consented).' })}</Bullet>
          <Bullet>{t('terms.s5.b2', { defaultValue: 'Generate content that violates the law where you operate — including but not limited to non-consensual imagery, malware, harassment, or fraud.' })}</Bullet>
          <Bullet>{t('terms.s5.b3', { defaultValue: 'Reverse engineer, scrape, or stress-test our service beyond what your plan permits.' })}</Bullet>
          <Bullet>{t('terms.s5.b4', { defaultValue: 'Resell or rebrand Knowlez without a written reseller agreement.' })}</Bullet>
        </Bullets>
        <p className="mt-3">
          {t('terms.s5.consequence', {
            defaultValue:
              'Material violation gives us the right to suspend a workspace pending investigation, and to terminate it if the violation is not cured. We always notify the workspace owner first — except when we are required by law not to.',
          })}
        </p>
      </Section>

      <Section id="plans" kicker="06" title={t('terms.s6.title', { defaultValue: 'Plans and billing' }) as string}>
        <Bullets>
          <Bullet>{t('terms.s6.b1', { defaultValue: 'Free, Growth ($24/seat/mo), Global ($59/seat/mo), Enterprise ($129/seat/mo). The price page on knowlez.com is the source of truth.' })}</Bullet>
          <Bullet>{t('terms.s6.b2', { defaultValue: 'Charges are billed monthly in advance. Cancel any time; we pro-rate the unused portion of the month.' })}</Bullet>
          <Bullet>{t('terms.s6.b3', { defaultValue: 'AI usage is billed at cost on top of seat fees (no markup). Workspaces can cap monthly AI spend; we abort calls at exactly 100% of the cap.' })}</Bullet>
          <Bullet>{t('terms.s6.b4', { defaultValue: 'A failed payment puts the workspace into read-only mode after 7 days. If payment is not restored within 30 days, the workspace is suspended; content is retained for another 60 days before deletion.' })}</Bullet>
        </Bullets>
      </Section>

      <Section id="onprem" kicker="07" title={t('terms.s7.title', { defaultValue: 'On-prem option' }) as string}>
        <p>
          {t('terms.s7.body', {
            defaultValue:
              'Enterprise customers may deploy Knowlez on their own infrastructure using the Docker Compose bundle. There is no license premium for on-prem; you pay the seat-month fee and run the infrastructure yourself. The on-prem bundle has zero external subprocessors — the entire stack runs in your environment.',
          })}
        </p>
      </Section>

      <Section id="warranty" kicker="08" title={t('terms.s8.title', { defaultValue: 'Service guarantees' }) as string}>
        <Bullets>
          <Bullet>{t('terms.s8.b1', { defaultValue: 'Growth and Global tiers target 99.9% monthly uptime. Enterprise targets 99.95%.' })}</Bullet>
          <Bullet>{t('terms.s8.b2', { defaultValue: 'Status updates are published on knowlez.com/status during any incident.' })}</Bullet>
          <Bullet>{t('terms.s8.b3', { defaultValue: 'Service credits for missed targets are 10% of the affected month\'s fees per 1% of missed availability, capped at one month of fees.' })}</Bullet>
        </Bullets>
        <p className="mt-3">
          {t('terms.s8.disclaimer', {
            defaultValue:
              'Beyond the explicit guarantees above, the service is provided "as is." Knowlez is not liable for indirect, consequential, or punitive damages. Total aggregate liability in any 12-month period is capped at the fees you paid us in that period.',
          })}
        </p>
      </Section>

      <Section id="termination" kicker="09" title={t('terms.s9.title', { defaultValue: 'Ending the agreement' }) as string}>
        <p>
          {t('terms.s9.body', {
            defaultValue:
              'Either party can end this agreement at any time with 30 days written notice. We export your workspace content on request within 30 days of termination, then delete it within 90 days unless retention is required by law.',
          })}
        </p>
      </Section>

      <Section id="law" kicker="10" title={t('terms.s10.title', { defaultValue: 'Governing law' }) as string}>
        <p>
          {t('terms.s10.body', {
            defaultValue:
              'These terms are governed by the laws of Japan. Disputes are first brought to good-faith negotiation; if unresolved, to binding arbitration in Tokyo (or in your jurisdiction if local consumer-protection law requires it).',
          })}
        </p>
      </Section>

      <Section id="contact" kicker="11" title={t('terms.s11.title', { defaultValue: 'Contact' }) as string}>
        <p>
          {t('terms.s11.body', {
            defaultValue: 'Questions: legal@knowlez.com. Notices: Knowlez Inc., postal address on the public Security page.',
          })}
        </p>
      </Section>
    </LegalLayout>
  );
}
